// @ts-strict-ignore
import playIcon from '@/assets/svg/play.svg'
import thumbArrow from '@/assets/svg/thumbArrow.svg'
import { clsx } from 'clsx'
import Link from 'next/link'
import productImagePlaceholder from 'public/product-image-placeholder.jpeg'
import { ReactNode, useRef, useState } from 'react'
import 'swiper/css'
import 'swiper/css/a11y'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/thumbs'
import { A11y, Navigation, Pagination, Thumbs } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { ThumbsOptions } from 'swiper/types/modules/thumbs'

import { useSwiperRef } from '@/shared/lib/hooks/useSwiperRef'
import { getBotInfo } from '@/shared/lib/seo'
import { Nullable } from '@/shared/lib/utils/utilsTypes'
import Flex from '@/shared/ui/Flex'
import { Col, Row } from '@/shared/ui/Grid'
import { Icon } from '@/shared/ui/icon'
import { Image } from '@/shared/ui/Image'

import { ProductTriggers } from '../../../product-triggers/ProductTriggers'
import { useProductSliderVideo } from '../../hooks/useProductSliderVideo'
import { getMediaUrl, ProductSliderMedia } from '../../media'

import cl from './thumbs-gallery.module.scss'

export type ThumbsGalleryProps = {
	media: Nullable<ProductSliderMedia[]>
	videosIds?: string[]
	title: string
	onClick?: () => void
	setActiveSlide?: (number) => void
	sections: string[]
	bottomContent?: ReactNode
}

const THUMB_WIDTH = 100
const THUMB_HEIGHT = 100
const MAIN_IMAGE_WIDTH = 520
const MAIN_IMAGE_HEIGHT = 520

const CHESTNIY_ZNAK_SECTIONS = [76, 86, 103, 115, 1626]

export const ThumbsGallery = ({
	title,
	media,
	onClick,
	setActiveSlide: setExternalActiveSlide,
	sections,
	bottomContent
}: ThumbsGalleryProps): JSX.Element => {
	const [thumbsSwiper, setThumbsSwiper] = useState<Nullable<ThumbsOptions[`swiper`]>>(null)

	const mainContainerRef = useRef<HTMLDivElement>(null)
	const [nextEl, nextElRef] = useSwiperRef<HTMLButtonElement>()
	const [prevEl, prevElRef] = useSwiperRef<HTMLButtonElement>()

	const hideChestnyZnak = sections.some(section => CHESTNIY_ZNAK_SECTIONS.includes(Number(section)))

	const isBot = getBotInfo('isDetected')
	const resolvedThumbsSwiper =
		thumbsSwiper && typeof thumbsSwiper !== 'string' && !thumbsSwiper.destroyed ? thumbsSwiper : null
	const { setActiveSlide: handleActiveSlideChange, getVideoProps } = useProductSliderVideo({
		media,
		onSlideChange: setExternalActiveSlide
	})

	const CHESTNIY_ZNAK_ICON = (
		<svg xmlns="http://www.w3.org/2000/svg" id="Layer_1" version="1.1" viewBox="0 0 110 110">
			<rect width="110" height="110" rx="15" ry="15" fill="#63666a" />
			<path
				fill="#f6f42e"
				d="M30,85h12v10h-12c-8.3,0-15-6.7-15-15v-12h10v12c0,2.8,2.2,5,5,5ZM25,30c0-2.8,2.2-5,5-5h12v-10h-12c-8.3,0-15,6.7-15,15v12h10v-12ZM85,80c0,2.8-2.2,5-5,5h-12v10h12c8.3,0,15-6.7,15-15v-12h-10v12ZM80,15h-12v10h12c2.8,0,5,2.2,5,5v12h10v-12c0-8.3-6.7-15-15-15ZM74.9,38.7l-25.8,25.8-14.3-14.3-7.1,7.1,21.4,21.4,32.9-32.9-7.1-7.1Z"
			/>
		</svg>
	)
	const hasThumbs = media && media.length > 1

	return (
		<section className={cl.thumbsGallery}>
			<Row gutter={20}>
				{/* Thumbs carousel */}
				{hasThumbs && (
					<Col xs={0} lg={6} xl={4}>
						<div className={cl.thumbsGallery__side} aria-label="Карусель миниатюр">
							<Swiper
								modules={[Thumbs, Navigation, A11y]}
								onSwiper={setThumbsSwiper}
								lazyPreloadPrevNext={2}
								direction={`vertical`}
								spaceBetween={0}
								slidesPerView={4}
								freeMode={true}
								watchSlidesProgress
								navigation={{ prevEl, nextEl }}
								loop>
								{media.map((item, index) => (
									<SwiperSlide key={`${item.type}-${item.src}`}>
										<Flex center full>
											{item.type === 'image' ? (
												<Image
													priority
													width={THUMB_WIDTH}
													height={THUMB_HEIGHT}
													src={getMediaUrl(item.src)}
													style={{ objectFit: 'contain' }}
													alt={`${title} photo ${index + 1}`}
													onClick={() => handleActiveSlideChange(index)}
												/>
											) : (
												<div
													className={cl.thumbsGallery__videoThumbWrap}
													onClick={() => handleActiveSlideChange(index)}>
													<video
														className={cl.thumbsGallery__videoThumb}
														poster={item.poster ? getMediaUrl(item.poster) : undefined}
														src={getMediaUrl(item.src)}
														preload={item.poster ? 'none' : 'metadata'}
														muted
														playsInline
														aria-label={`${title} video ${index + 1}`}
													/>
													<span className={cl.thumbsGallery__videoThumbPlay} aria-hidden="true">
														<Icon icon={playIcon} />
													</span>
												</div>
											)}
										</Flex>
									</SwiperSlide>
								))}
							</Swiper>

							{/* Prev/Next buttons */}
							<button
								className={clsx(cl.thumbsGallery__navButton, cl[`thumbsGallery__navButton--prev`])}
								aria-label="Пролистать на 1 назад"
								ref={prevElRef}>
								<Icon icon={thumbArrow} />
							</button>
							<button
								className={clsx(cl.thumbsGallery__navButton, cl[`thumbsGallery__navButton--next`])}
								aria-label="Пролистать на 1 вперед"
								ref={nextElRef}>
								<Icon icon={thumbArrow} />
							</button>
						</div>
						{/* There was YouTube's videos thumbs */}
					</Col>
				)}
				{/* Main carousel */}
				<Col xs={24} lg={hasThumbs ? 18 : 24} xl={hasThumbs ? 20 : 24}>
					<div
						className={cl.thumbsGallery__main}
						ref={mainContainerRef}
						aria-label={`Карусель изображений товара «${title}»`}>
						{hideChestnyZnak && (
							<Link
								prefetch={false}
								href="/news/chestnyj-znak/"
								title="Честный знак"
								className={cl.chestniy_znak_link}
								target="_blank">
								{CHESTNIY_ZNAK_ICON}
							</Link>
						)}

						{media && !!media.length ? (
							<Swiper
								modules={[Thumbs, Pagination, A11y]}
								spaceBetween={0}
								grabCursor={true}
								loop
								thumbs={{
									swiper: resolvedThumbsSwiper
								}}
								pagination={{ clickable: true }}
								onSlideChange={swiper => handleActiveSlideChange(swiper.realIndex)}>
								{media.map((item, index) => (
									<SwiperSlide
										key={`${item.type}-${item.src}`}
										onClick={item.type === 'image' ? () => onClick?.() : undefined}>
										<Flex center full>
											{item.type === 'image' ? (
												<Image
													onClick={() => handleActiveSlideChange(index)}
													priority={isBot || index === 0}
													loading={isBot || index === 0 ? `eager` : `lazy`}
													fetchPriority={isBot || index === 0 ? `high` : `auto`}
													placeholder="blur"
													width={MAIN_IMAGE_WIDTH}
													height={MAIN_IMAGE_HEIGHT}
													src={getMediaUrl(item.src)}
													alt={`${title} photo ${index + 1}`}
												/>
											) : (
												<video
													className={cl.thumbsGallery__video}
													controls
													aria-label={`${title} video ${index + 1}`}
													{...getVideoProps(index, item.src, item.poster)}
												/>
											)}
										</Flex>
									</SwiperSlide>
								))}
							</Swiper>
						) : (
							<Flex full center>
								<Image alt="Картинка заглушка" width={250} height={250} src={productImagePlaceholder} />
							</Flex>
						)}
					</div>
				</Col>
			</Row>
			{bottomContent ?? <ProductTriggers />}
		</section>
	)
}
